import React, { useState } from 'react';
import { useEditor } from '@tiptap/react';
import { StackProps, VStack } from '@chakra-ui/react';

import { useGlobalStyles } from '../../support/useGlobalStyles';

import MenuBar, { CustomButtonConfig } from './MenuBar';
import {
  EditorContent,
  createEditorContent,
  getEditorContent,
} from './EditorContent';
import { getExtensions } from './extensions/extensions';
import { cleanPasteFromSlice } from './helpers';
import { EditorPane } from './EditorPane';
import { editorCss, rootClassName } from './css';

type Props = Omit<StackProps, 'onChange'> & {
  basicToolbar?: boolean;
  initialContent?: EditorContent | string;
  onChange?: (content: EditorContent) => void;
  readOnly?: boolean;
  contentClassName?: string;
  extraButtons?: Array<CustomButtonConfig>;
};

export default function StatefulEditor(props: Props) {
  const {
    basicToolbar,
    height,
    initialContent,
    onChange,
    readOnly,
    contentClassName,
    extraButtons,
    ...otherProps
  } = props;
  useGlobalStyles(editorCss);
  const [initialContentResolved] = useState(() => {
    return initialContent === undefined ? null : initialContent.toString();
  });
  const editor = useEditor({
    editorProps: {
      transformPasted: (slice) => cleanPasteFromSlice(slice),
    },
    extensions: getExtensions(),
    content: initialContentResolved,
    onUpdate: ({ editor }) => {
      const content = getEditorContent(editor);
      onChange?.(content);
    },
    editable: !readOnly,
  });

  if (!editor) {
    return null;
  }

  return (
    <VStack align="stretch" {...otherProps}>
      <MenuBar
        editor={editor}
        basicToolbar={basicToolbar}
        extraButtons={extraButtons}
      />
      <EditorPane height={height} className={rootClassName} editor={editor} />
    </VStack>
  );
}

export { createEditorContent };
export type { EditorContent };
